import {
  StreamLainAnimationController,
  streamLainAnimationController,
  type StreamLainAnimationHandle,
  type StreamLainMotionKind,
} from '@blocksuite/affine-shared/utils';
import { startOfDay } from 'date-fns/startOfDay';
import { startOfWeek } from 'date-fns/startOfWeek';

/** Marks the parts of a calendar grid that travel when the period changes. */
export const CALENDAR_PERIOD_SLIDE_ATTR = 'data-calendar-period-slide';

/** Share of the calendar width the incoming days travel. */
const CALENDAR_PERIOD_SLIDE_DISTANCE = 0.3;

/** First moment of the day, week (Monday) or month the cursor shows. */
export function calendarPeriodStart(mode: string, cursor: Date): Date {
  if (mode === 'week') return startOfWeek(cursor, { weekStartsOn: 1 });
  if (mode === 'month') {
    return new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  }
  return startOfDay(cursor);
}

/**
 * Period change motion (previous, next, today, a picked day). Only the day
 * headers, the all-day lane and the day columns slide in from the travel
 * side; the time axis, scrollbars and everything around the calendar stay
 * put, and the scroll containers clip the motion. A mode change is not a
 * period change and does not slide. The canvas, fullscreen and Planner
 * calendars share this motion.
 */
export class CalendarPeriodMotion {
  private mode: string | undefined;
  private period: string | undefined;
  private time = 0;
  private readonly handles = new Map<Element, StreamLainAnimationHandle>();

  constructor(
    private readonly controller: StreamLainAnimationController = streamLainAnimationController
  ) {}

  /** Call after each render with the shown mode and cursor day. */
  settle(
    root: HTMLElement | null | undefined,
    mode: string,
    cursor: Date
  ): void {
    const time = calendarPeriodStart(mode, cursor).getTime();
    const period = String(time);
    const previousMode = this.mode;
    const previousPeriod = this.period;
    const previousTime = this.time;
    this.mode = mode;
    this.period = period;
    this.time = time;
    if (!root || previousPeriod === undefined) return;
    if (previousMode !== mode) {
      const surface = root.querySelector<HTMLElement>(
        '[data-calendar-surface]'
      );
      if (surface)
        this.play(surface, [{ opacity: 0 }, { opacity: 1 }], 'enter');
      return;
    }
    if (previousPeriod === period) return;
    const distance =
      Math.round(root.clientWidth * CALENDAR_PERIOD_SLIDE_DISTANCE) *
      (time > previousTime ? 1 : -1);
    for (const element of root.querySelectorAll(
      `[${CALENDAR_PERIOD_SLIDE_ATTR}]`
    )) {
      this.play(
        element,
        [
          { opacity: 0, transform: `translateX(${distance}px)` },
          { opacity: 1, transform: 'translateX(0)' },
        ],
        'slide'
      );
    }
  }
  private play(
    element: Element,
    keyframes: Keyframe[],
    kind: StreamLainMotionKind
  ): void {
    const handle = this.controller.play(element, keyframes, kind);
    this.handles.set(element, handle);
    void handle.finished.then(() => {
      if (this.handles.get(element) === handle) this.handles.delete(element);
    });
  }
  dispose(): void {
    this.handles.forEach(handle => handle.cancel());
    this.handles.clear();
    this.mode = undefined;
    this.period = undefined;
  }
}
